import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import type { InstallationCreateWizardStepMeta } from '@/features/installations/components/installation-create/types'

type InstallationCreateWizardStepsOverviewProps = {
  stepMetas: InstallationCreateWizardStepMeta[]
  currentStepIndex: number
}

export function InstallationCreateWizardStepsOverview({
  stepMetas,
  currentStepIndex,
}: InstallationCreateWizardStepsOverviewProps) {
  const { t } = useTranslation('installations')

  return (
    <ol
      className="flex flex-col gap-0 sm:flex-row sm:flex-wrap sm:gap-6"
      aria-label={t('create.wizard.overviewAriaLabel')}
    >
      {stepMetas.map((item, index) => {
        const visualStepNumber = index + 1
        const isComplete = currentStepIndex > index
        const isCurrent = currentStepIndex === index

        return (
          <li
            key={item.key}
            className="flex min-w-0 flex-1 gap-3 sm:max-w-[200px] sm:flex-1 sm:flex-col sm:gap-2"
            aria-current={isCurrent ? 'step' : undefined}
          >
            <div className="flex items-start gap-3 sm:flex-col sm:items-center sm:text-center">
              <span
                className={cn(
                  'flex size-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                  isComplete &&
                    'border-primary bg-primary text-primary-foreground',
                  isCurrent &&
                    !isComplete &&
                    'border-primary bg-background text-primary',
                  !isCurrent &&
                    !isComplete &&
                    'border-muted-foreground/35 text-muted-foreground',
                )}
              >
                {isComplete ? (
                  <Check className="size-4" strokeWidth={2.5} aria-hidden />
                ) : (
                  visualStepNumber
                )}
              </span>
              <div className="min-w-0 flex-1 sm:w-full">
                <p
                  className={cn(
                    'text-sm font-medium leading-snug',
                    isCurrent && 'text-foreground',
                    !isCurrent && 'text-muted-foreground',
                  )}
                >
                  {item.title}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground sm:mt-1">
                  {item.description}
                </p>
              </div>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
